import { FormEvent, ChangeEvent, useRef, useCallback, RefObject } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import * as IconMUI from '@mui/icons-material';
import useStyles from './styles';
import clsx from 'clsx';

/**
 * Searchbar component provides a search input field with search icon and clear button.
 * Used in the Appbar (Desktop and Mobile) for site-wide search functionality.
 * Displays a clear icon when there's input text and handles form submission.
 *
 * @param props - Component props
 * @param props.searchInput - Current search input value
 * @param props.handleSearchInput - Callback function called when input value changes
 * @param props.onSubmit - Callback function called when form is submitted
 * @param props.rootContainerClass - Optional CSS class for the root container
 * @returns Searchbar component
 */
const Searchbar = ({
  searchInput,
  handleSearchInput,
  onSubmit,
  rootContainerClass,
}: SearchBarProps) => {
  const { classes } = useStyles();
  const inputRef: RefObject<HTMLInputElement> = useRef(null);

  const handleChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      handleSearchInput(value);
    },
    [handleSearchInput],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      inputRef.current?.blur();
      event.preventDefault();
      onSubmit(event);
    },
    [onSubmit],
  );

  const handleClear = useCallback(() => {
    handleSearchInput('');
  }, [handleSearchInput]);

  return (
    <Box
      className={clsx(classes.root, rootContainerClass)}
      component="div"
      data-component="Searchbar"
      suppressHydrationWarning
    >
      <form
        onSubmit={handleSubmit}
        className={classes.inputStyling}
        suppressHydrationWarning
      >
        <InputBase
          type="text"
          id="search"
          inputRef={inputRef}
          value={searchInput}
          onChange={handleChange}
          className={classes.inputStyling}
          inputProps={{
            className: classes.inputStyling,
            'aria-label': 'Search site content',
          }}
          placeholder="Search"
          name="search"
          suppressHydrationWarning
          startAdornment={
            <InputAdornment position="start" suppressHydrationWarning>
              <IconMUI.Search fontSize="small" className={classes.searchIcon} />
            </InputAdornment>
          }
          endAdornment={
            searchInput.length > 0 && (
              <InputAdornment
                position="end"
                onClick={handleClear}
                suppressHydrationWarning
              >
                <IconMUI.Clear
                  fontSize="small"
                  className={classes.clearIcon}
                  aria-label="Clear search"
                />
              </InputAdornment>
            )
          }
        />
      </form>
    </Box>
  );
};

export interface SearchBarProps {
  /** Current search input value */
  searchInput: string;
  /** Callback function called when input value changes */
  handleSearchInput: (input: string) => void;
  /** Callback function called when form is submitted */
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  /** Optional CSS class for the root container */
  rootContainerClass?: string;
}

export default Searchbar;
